<template>
	<div class="d-flex justify-center" v-if="fields_loading">
		<v-progress-circular class="d-flex justify-center"></v-progress-circular>
	</div>

	<v-form
		v-else
		ref="form"
		class="registration-form-step2"
		v-model="valid"
		lazy-validation
		@submit.prevent="submitForm"
	>
		<div
			v-for="(fieldsGroup, sectionName) in groupedFields"
			:key="sectionName"
			class="registration-form-step2__section"
		>
			<h3 class="registration-form-step2__section-title" v-if="sectionName !== 'None'">
				{{ sectionName }}
			</h3>

			<v-row>
				<v-col
					v-for="(fieldItem, index) in fieldsGroup"
					:key="fieldItem.field"
					class="py-0"
					:cols="getColsForIndex(index, fieldsGroup.length)"
				>
					<v-file-input
						v-if="fieldItem.type === 'user_logo'"
						class="pt-0 field"
						:class="{ 'field-required': isRequired(fieldItem.required) }"
						:value="getFieldValue(fieldItem)"
						@change="setFieldValue(fieldItem, $event)"
						:error-messages="field_errors[fieldItem.field]"
						:rules="getFieldRules(fieldItem)"
						:placeholder="fieldItem.title"
						:required="isRequired(fieldItem.required)"
						color="red"
						accept="image/*"
						truncate-length="40"
					/>

					<v-textarea
						v-else-if="fieldItem.type === 'html'"
						class="pt-0 field"
						:class="{ 'field-required': isRequired(fieldItem.required) }"
						:value="getFieldValue(fieldItem)"
						@input="setFieldValue(fieldItem, $event)"
						:error-messages="field_errors[fieldItem.field]"
						:rules="getFieldRules(fieldItem)"
						:placeholder="fieldItem.title"
						:required="isRequired(fieldItem.required)"
						color="red"
						auto-grow
					/>
					<v-select
						v-else-if="fieldItem.type === 'list'"
						:multiple="isMultiple(fieldItem)"
						:error-messages="field_errors[fieldItem.field]"
						class="pt-0 field"
						:class="{ 'field-required': isRequired(fieldItem.required) }"
						:required="isRequired(fieldItem.required)"
						@input="setFieldValue(fieldItem, $event)"
						:items="
							getListItems(fieldItem.source).concat(
								shouldShowLoader(fieldItem.source) ? [{ __loader: true }] : []
							)
						"
						:placeholder="fieldItem.title"
						:value="getFieldValue(fieldItem)"
						item-text="val"
						item-value="id"
						:item-disabled="(item) => item.__loader"
						:menu-props="{ contentClass: 'select-menu-content', maxHeight: 300 }"
					>
						<template v-slot:item="{ item }">
							<div
								v-if="item.__loader"
								style="display: flex; justify-content: center; min-height: 1px"
								v-intersect="{
									handler: () => loadMore(fieldItem.source),
									options: { rootMargin: '0px', threshold: 1.0 },
								}"
							>
								<v-progress-circular indeterminate size="20" />
							</div>
							<span v-else>{{ item.val }}</span>
						</template>
					</v-select>

					<v-text-field
						v-else-if="fieldItem.type === 'phone'"
						class="pt-0 field"
						:class="{ 'field-required': isRequired(fieldItem.required) }"
						:value="getFieldValue(fieldItem)"
						@input="setFieldValue(fieldItem, $event)"
						:error-messages="field_errors[fieldItem.field]"
						:rules="getFieldRules(fieldItem)"
						:placeholder="fieldItem.title"
						:required="isRequired(fieldItem.required)"
						color="red"
						type="tel"
					/>

					<v-text-field
						v-else-if="fieldItem.type === 'email'"
						class="pt-0 field"
						:class="{ 'field-required': isRequired(fieldItem.required) }"
						:value="getFieldValue(fieldItem)"
						@input="setFieldValue(fieldItem, $event)"
						:error-messages="field_errors[fieldItem.field]"
						:rules="getFieldRules(fieldItem)"
						:placeholder="fieldItem.title"
						:required="isRequired(fieldItem.required)"
						color="red"
						type="email"
					/>

					<v-text-field
						v-else
						class="pt-0 field"
						:class="{ 'field-required': isRequired(fieldItem.required) }"
						:value="getFieldValue(fieldItem)"
						@input="setFieldValue(fieldItem, $event)"
						:error-messages="field_errors[fieldItem.field]"
						:rules="getFieldRules(fieldItem)"
						:placeholder="fieldItem.title"
						:required="isRequired(fieldItem.required)"
						color="red"
						:type="fieldItem.type === 'url' ? 'url' : 'text'"
					/>
				</v-col>
			</v-row>
		</div>
		<v-btn
			v-if="registration_fields.length > 0"
			class="registration-form-step2__submit elevation-0"
			type="submit"
			:loading="isLoading"
		>
			{{ signUpButtonTitle }}
		</v-btn>
		<v-snackbar v-model="showErrorSnackbar" color="red" timeout="4000" top>
			{{ errorMessage }}
		</v-snackbar>
	</v-form>
</template>

<script>
import { mapGetters, mapActions } from 'vuex'
import Form from '@/mixins/Form'

export default {
	name: 'RegistrationFormStep2',
	mixins: [Form],
	props: {
		initFields: { type: Object, default: () => ({}) },
		categorySettings: { type: Object, default: null },
	},

	data() {
		return {
			loadingPages: {},
			showErrorSnackbar: false,
			errorMessage: '',
			defaultFields: [
				{
					field: 'user_fname',
					required: 1,
					section: 'Profile Info',
					title: 'First Name',
					type: 'string',
					value: null,
				},
				{
					field: 'user_lname',
					required: 1,
					section: 'Profile Info',
					title: 'Last Name',
					type: 'string',
					value: null,
				},
			],
			fields: {
				slug: '',
				user_category_id: '',
				user_logo: [],
			},
			valid: false,
			field_errors: {},
		}
	},

	computed: {
		...mapGetters('settings', [
			'registration_fields',
			'fields_loading',
			'listDataBySource',
			'listDataBySourceLoading',
			'paginationState',
		]),
		...mapGetters('auth', ['isLoading']),

		signUpButtonTitle() {
			return this.categorySettings?.signup_button || 'Sign Up'
		},
		groupedFields() {
			return this.registration_fields.reduce((acc, item) => {
				const section = item.section || 'General'
				;(acc[section] = acc[section] || []).push(item)
				return acc
			}, {})
		},
	},
	watch: {
		registration_fields: {
			immediate: true,
			handler(fields) {
				if (!Array.isArray(fields)) return

				const uniqueSources = [
					...new Set(
						fields.filter((f) => f.type === 'list' && f.source).map((f) => f.source)
					),
				]

				uniqueSources.forEach((source) => {
					this.getListBySource({ source })
				})
			},
		},
	},

	methods: {
		...mapActions('auth', ['signUp']),
		...mapActions('settings', ['getRegistrationFields', 'getListBySource']),
		isMultiple(fieldItem) {
			return fieldItem.type === 'list' && fieldItem.amount !== 1
		},

		async loadMore(source) {
			const pagination = this.paginationState?.[source]
			const currentPage = Number(pagination?.page)
			const totalPages = Number(pagination?.pageCount)

			if (!currentPage || !totalPages || currentPage >= totalPages) return
			const nextPage = currentPage + 1

			if (this.loadingPages[source]?.includes(nextPage)) return
			this.$set(this.loadingPages, source, [...(this.loadingPages[source] || []), nextPage])

			await this.getListBySource({ source, page: nextPage })
		},

		isRequired(value) {
			return Number(value) === 1
		},
		getListItems(source) {
			const listField = this.listDataBySource?.[source] || []
			return listField.filter((item) => item.id !== '0')
		},

		getColsForIndex(index, totalCount) {
			return totalCount % 2 !== 0 && index === totalCount - 1 ? 12 : 6
		},
		shouldShowLoader(source) {
			const pagination = this.paginationState?.[source]
			const currentPage = Number(pagination?.page)
			const totalPages = Number(pagination?.pageCount)
			return currentPage && totalPages && currentPage < totalPages
		},
		getFieldRules(fieldItem) {
			const rules = []
			if (this.isRequired(fieldItem.required)) {
				rules.push((v) => (v !== '' && v != null) || 'This field is required.')
			}
			if (fieldItem.type === 'list') {
				rules.push((v) => {
					if (
						!this.isRequired(fieldItem.required) &&
						(v === '' || v == null || (Array.isArray(v) && v.length === 0))
					) {
						return true
					}

					if (this.isMultiple(fieldItem)) {
						return (
							(Array.isArray(v) && v.length > 0) ||
							'Please choose at least one value.'
						)
					}

					return !!v || 'Please choose a value.'
				})
			}

			return rules
		},

		getFieldValue(fieldItem) {
			return this.fields[fieldItem.field] ?? ''
		},

		setFieldValue(fieldItem, value) {
			this.$set(this.fields, fieldItem.field, value)
		},

		async submitForm() {
			const ok = await this.$refs.form.validate()
			if (!ok) {
				this.errorMessage = 'Please fill in all required fields'
				this.showErrorSnackbar = true
				return
			}

			const payload = {
				...this.fields,
				slug: this.$route.query.slug,
				user_category_id: this.$route.query.user_category_id,
			}
			Object.keys(payload).forEach((key) => {
				const value = payload[key]
				if (
					value === '(None selected)' ||
					value === 'None selected' ||
					value === '(Not set)'
				) {
					payload[key] = ''
				}
				if (
					Array.isArray(value) &&
					value.some(
						(v) => v === '(None selected)' || v === 'None selected' || v === '(Not set)'
					)
				) {
					payload[key] = value.filter(
						(v) => v !== '(None selected)' && v !== 'None selected' && v !== '(Not set)'
					)
				}
			})

			const res = await this.signUp(payload)
			res
				? this.$router.push({
						name: 'login',
						query: {
							slug: this.$route.query.slug,
							user_category_id: this.$route.query.user_category_id,
						},
				  })
				: this.$emit('stepBack')
		},
	},

	async created() {
		await this.getRegistrationFields(this.$route.query.user_category_id)
		Object.keys(this.initFields).forEach((key) => {
			this.$set(this.fields, key, this.initFields[key])
		})
		this.registration_fields.forEach((f) => {
			if (this.fields[f.field] === undefined) {
				const def = f.type === 'user_logo' ? [] : this.isMultiple(f) ? [] : f.value || ''
				this.$set(this.fields, f.field, def)
			}
			this.$set(this.field_errors, f.field, null)
		})
	},
}
</script>

<style lang="scss">
.registration-form-step2 {
	&__section {
		margin-top: 1rem;

		&-title {
			font-size: 18px;
			font-weight: 700;
			color: #4a4a4a;
			margin-bottom: 12px;
		}
	}

	.v-select__slot {
		font-size: 13px;
		font-weight: 500;
	}

	&__submit {
		width: 100%;
		height: 50px !important;
		border-radius: 8px;
		font-size: 18px;
		font-weight: 700;
		color: #fff !important;
		text-transform: capitalize;
		background-color: var(--button-main-background-color) !important;
		margin-top: 32px;
	}

	.field-required::after {
		content: ' *';
		color: red;
	}

	.field {
		input {
			font-size: 13px;
			font-weight: 500;
			max-height: 50px;
			padding-left: 10px;
		}

		.v-input__slot {
			border-bottom: 1px solid rgba(0, 0, 0, 0.6);
		}
	}

	input::placeholder {
		color: #9b9b9b !important;
		opacity: 1;
	}
}
.select-menu-content {
	max-height: 300px !important;
	overflow-y: auto !important;
	max-width: 250px !important;
	min-width: 200px !important;
}
.v-select__selections {
	flex-wrap: nowrap !important;
}
</style>
